import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CopyIcon, type CopyIconHandle } from "@/components/ui/copy-icon";
import { TrashIcon, type TrashIconHandle } from "@/components/ui/trash";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toastError } from "../../utils/showApiErrorToast";
import {
  Box,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { toast } from "sonner";
import {
  deleteReportStrategyComposedPinescript,
  getReportStrategyComposedPinescript,
  getReportStrategyIndicatorPinescript,
  ReportStrategyComposedPineError,
  ReportsApiError,
} from "../../services/reportsApi";
import type { ReportStrategyPolicyVersion, ReportStrategyResponse } from "../../types/reports";
import type { ComposeOutputMode, ReportStrategyPineParseResult } from "../../types/reportStrategyPine";
import {
  StrategyComposeModal,
  COMPOSE_DIALOG_DESCRIPTION,
  STRATEGY_COMPOSE_DIALOG_CONTENT_CLASS,
  type EmbeddedComposePresentation,
} from "./StrategyComposeModal";
import { PineScriptCodeBlock } from "../Reports/PineScriptCodeBlock";
import { PineScriptScrambleReveal } from "../Reports/PineScriptScrambleReveal";
import {
  activeComposedScriptDescription,
  activeComposedScriptTitle,
  isSeparateIndicatorComposeMode,
  canDownloadActiveComposedPine,
  extractPineScriptText,
  activePineProvenanceParts,
  loadStoredComposeMode,
  strategyHasTradingViewPinescriptCreated,
} from "../../utils/reportStrategyPineUi";
import { ActivePineProvenanceText } from "./ActivePineProvenanceText";
import { PINE_DETAIL_DIALOG_CLOSE_MS, StrategyPineDetailDialog } from "./StrategyPineDetailDialog";
import { DeleteActivePinescriptDialog } from "./DeleteActivePinescriptDialog";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
  StrategyPineSectionCard,
} from "./StrategyPineSectionCard";
import { activePineProvenanceMismatch } from "../../utils/strategyComposeUi";
import { isIndicatorPineScript } from "../../utils/pineIrUi";
import { activePineAccordionHeaderBadge } from "../../views/Dashboard/Strategies/strategyTablePineBadges";
import { buildActiveTradingViewComposeChecklist } from "../../utils/activeTradingViewComposeChecklist";
import { ActiveTradingViewPineComposeChecklist } from "./ActiveTradingViewPineComposeChecklist";
import { ActiveTradingViewPineComposeRemainingAlert } from "./ActiveTradingViewPineComposeRemainingAlert";
import {
  STRATEGY_PINE_ALERT_CLASS,
  StrategyPineAlertIcon,
  strategyPineAlertVariant,
} from "./strategyPineAlertUi";

/** Match StrategyPineScriptViewDialog code panel height. */
const PINE_SCRIPT_BLOCK_MAX_H = "min(60vh, 520px)";

/** Inline preview on the strategy overview active pine card. */
const PINE_SCRIPT_CARD_MAX_H = "min(280px, 38vh)";

function PineScriptDeleteIconButton({
  disabled,
  ariaLabel,
  onClick,
}: {
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}): React.ReactElement {
  const deleteIconRef = useRef<TrashIconHandle | null>(null);

  const startDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.startAnimation();
  }, []);

  const stopDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.stopAnimation();
  }, []);

  const deleteIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startDeleteIconAnimation,
      onMouseEnter: startDeleteIconAnimation,
      onMouseLeave: stopDeleteIconAnimation,
    }),
    [startDeleteIconAnimation, stopDeleteIconAnimation]
  );

  return (
    <button
      type="button"
      data-pine-script-delete=""
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      {...deleteIconHoverHandlers}
    >
      <TrashIcon ref={deleteIconRef} size={16} className="shrink-0 text-current" aria-hidden />
    </button>
  );
}

function PineScriptCopyIconButton({
  disabled,
  ariaLabel,
  onClick,
}: {
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}): React.ReactElement {
  const copyIconRef = useRef<CopyIconHandle | null>(null);

  const startCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.startAnimation();
  }, []);

  const stopCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.stopAnimation();
  }, []);

  const copyIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startCopyIconAnimation,
      onMouseEnter: startCopyIconAnimation,
      onMouseLeave: stopCopyIconAnimation,
    }),
    [startCopyIconAnimation, stopCopyIconAnimation]
  );

  return (
    <button
      type="button"
      data-pine-script-copy=""
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      {...copyIconHoverHandlers}
    >
      <CopyIcon ref={copyIconRef} size={16} className="shrink-0 text-current" aria-hidden />
    </button>
  );
}

function ActivePineScriptPanel({
  title,
  description,
  code,
  emptyLabel,
  showTitle = true,
  codeMaxH = PINE_SCRIPT_BLOCK_MAX_H,
}: {
  title: string;
  description: string;
  code: string;
  emptyLabel: string;
  showTitle?: boolean;
  codeMaxH?: string;
}): React.ReactElement {
  const trimmed = code.trim();

  return (
    <Field className="w-full" data-strategy-active-pine-script-field="">
      {showTitle ? (
        <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{title}</FieldLabel>
      ) : null}
      <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{description}</FieldDescription>
      {trimmed ? (
        <PineScriptCodeBlock code={code} maxH={codeMaxH} brandChrome />
      ) : (
        <Text fontSize="sm" color="gray.500" _dark={{ color: "gray.400" }} fontFamily="var(--font-primary)">
          {emptyLabel}
        </Text>
      )}
    </Field>
  );
}

type PineDetailDialogMode = "checklist" | "compose";

export type StrategyActiveTradingViewPineProps = {
  strategyId: string;
  strategy: ReportStrategyResponse;
  currentPolicyVersionId?: string | null;
  activePolicyVersionCount?: number;
  subtleText: string;
  textColor: string;
  borderColor: string;
  composeVersions: ReportStrategyPolicyVersion[];
  preferredComposePolicyVersionId?: string | null;
  parseResult?: ReportStrategyPineParseResult | null;
  onStrategyRefresh?: () => void | Promise<void>;
  onComposeSuccess?: (composedPolicyVersionId: string) => void | Promise<void>;
  autoOpenSignal?: number;
  autoOpenMode?: PineDetailDialogMode;
  /** Clears the parent auto-open signal after this card consumes it once. */
  onAutoOpenConsumed?: () => void;
  /** Reverts a pending policy-version switch when compose is dismissed. */
  onComposeCancel?: () => void;
  onNavigateToPoliciesTab?: () => void;
  /** Parent-triggered compose start (e.g. standalone compose modal on strategy detail). */
  composeStartSignal?: number;
  composeFailedSignal?: number;
  /** Parent hook when compose begins (e.g. switch to Overview tab). */
  onComposeStarted?: () => void;
};

type LoadedScripts = {
  strategyScript: string;
  indicatorScript: string;
  outputMode?: ComposeOutputMode | string;
};

type ActivePineScriptTab = "strategy" | "indicator";

function ActivePineLoadedScripts({
  scripts,
  composeMode,
  activeTab,
  onActiveTabChange,
  codeMaxH = PINE_SCRIPT_BLOCK_MAX_H,
}: {
  scripts: LoadedScripts;
  composeMode?: ComposeOutputMode | string | null;
  activeTab: ActivePineScriptTab;
  onActiveTabChange: (tab: ActivePineScriptTab) => void;
  codeMaxH?: string;
}): React.ReactElement {
  const separateMode =
    isSeparateIndicatorComposeMode(composeMode) || Boolean(scripts.indicatorScript.trim());
  const composedDescription = activeComposedScriptDescription(composeMode, "composed");
  const strategyTitle = activeComposedScriptTitle(composeMode, separateMode);
  const indicatorDescription = activeComposedScriptDescription(composeMode, "indicator_overlay");

  if (!separateMode) {
    return (
      <ActivePineScriptPanel
        title={strategyTitle}
        description={composedDescription}
        code={scripts.strategyScript}
        emptyLabel="No strategy script returned."
        codeMaxH={codeMaxH}
      />
    );
  }

  return (
    <Tabs
      data-trade-log-tabs=""
      value={activeTab}
      onValueChange={(value) => onActiveTabChange(value as ActivePineScriptTab)}
      className="!mb-3 w-full min-w-0"
    >
      <div className="w-full overflow-x-auto overflow-y-hidden">
        <TabsList className="min-w-max">
          <TabsTrigger value="strategy">Strategy</TabsTrigger>
          <TabsTrigger value="indicator">Indicator</TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="strategy" className="mt-0 min-w-0 outline-none">
        <ActivePineScriptPanel
          title={strategyTitle}
          description={composedDescription}
          code={scripts.strategyScript}
          emptyLabel="No strategy script returned."
          showTitle={false}
          codeMaxH={codeMaxH}
        />
      </TabsContent>

      <TabsContent value="indicator" className="mt-0 min-w-0 outline-none">
        <ActivePineScriptPanel
          title="Tessera indicator overlay"
          description={indicatorDescription}
          code={scripts.indicatorScript}
          emptyLabel="No indicator overlay returned. Re-compose in separate indicator mode to regenerate it."
          showTitle={false}
          codeMaxH={codeMaxH}
        />
      </TabsContent>
    </Tabs>
  );
}

export function StrategyActiveTradingViewPine({
  strategyId,
  strategy,
  currentPolicyVersionId,
  activePolicyVersionCount = 0,
  subtleText,
  textColor,
  borderColor,
  composeVersions,
  preferredComposePolicyVersionId,
  parseResult,
  onStrategyRefresh,
  onComposeSuccess,
  autoOpenSignal,
  autoOpenMode = "checklist",
  onAutoOpenConsumed,
  onComposeCancel,
  onNavigateToPoliciesTab,
  composeStartSignal = 0,
  composeFailedSignal = 0,
  onComposeStarted,
}: StrategyActiveTradingViewPineProps): React.ReactElement {
  const history = useHistory();
  const cardAnchorRef = useRef<HTMLDivElement | null>(null);
  const composeDismissSuppressedRef = useRef(false);
  const active = strategy.active_pine;
  const status = active?.status ?? "none";
  const provenanceParts = activePineProvenanceParts(active);
  const scriptKindForMessage: "indicator" | "strategy" = isIndicatorPineScript(strategy) ? "indicator" : "strategy";
  const provenanceMismatch = activePineProvenanceMismatch(active, currentPolicyVersionId, scriptKindForMessage);
  const hasGeneratedPine = canDownloadActiveComposedPine(strategy);
  const hasComposedExport = strategyHasTradingViewPinescriptCreated(strategy);
  const showEmptyState = status === "none" || (!hasGeneratedPine && status !== "stale");
  const showComposeChecklist = !hasComposedExport;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<PineDetailDialogMode>("checklist");
  const [composeFooter, setComposeFooter] = useState<ReactNode>(null);
  const [composePresentation, setComposePresentation] =
    useState<EmbeddedComposePresentation | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scripts, setScripts] = useState<LoadedScripts | null>(null);
  const [activePineScriptTab, setActivePineScriptTab] = useState<ActivePineScriptTab>("strategy");
  const [deletingComposedPine, setDeletingComposedPine] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardScriptsPending, setCardScriptsPending] = useState(false);
  const [composeRevealActive, setComposeRevealActive] = useState(false);
  const [composeInFlight, setComposeInFlight] = useState(false);
  const [composeSequence, setComposeSequence] = useState(0);

  useEffect(() => {
    setDialogOpen(false);
    setDialogMode("checklist");
    setComposeFooter(null);
    setComposePresentation(null);
    setCardScriptsPending(false);
    setComposeRevealActive(false);
    setComposeInFlight(false);
  }, [strategyId]);

  useEffect(() => {
    setScripts(null);
    setLoadError(null);
    setLoading(false);
    setActivePineScriptTab("strategy");
  }, [strategy.updated_at, status, strategy.has_composed_pine, strategy.has_indicator_pine]);

  useEffect(() => {
    setActivePineScriptTab("strategy");
  }, [strategyId]);

  useEffect(() => {
    if (!hasGeneratedPine) {
      setScripts(null);
      setLoadError(null);
      setLoading(false);
    }
  }, [hasGeneratedPine]);

  const loadScripts = useCallback(async () => {
    if (!hasGeneratedPine) return;
    setLoading(true);
    setLoadError(null);
    try {
      const composed = await getReportStrategyComposedPinescript(strategyId);
      const outputMode =
        (composed.meta?.output_mode as ComposeOutputMode | undefined) ??
        (active?.last_compose_mode as ComposeOutputMode | undefined) ??
        loadStoredComposeMode(strategyId) ??
        undefined;
      const needsIndicatorOverlay =
        isSeparateIndicatorComposeMode(outputMode) || Boolean(strategy.has_indicator_pine);

      let indicatorScript = "";
      if (needsIndicatorOverlay) {
        try {
          const indicator = await getReportStrategyIndicatorPinescript(strategyId);
          indicatorScript = extractPineScriptText(indicator);
        } catch (e) {
          if (e instanceof ReportStrategyComposedPineError && e.code === "no_composed_pine") {
            if (isSeparateIndicatorComposeMode(outputMode)) {
              setLoadError(
                "Strategy snapshot loaded, but the Tessera indicator overlay is missing. Try composing again with separate indicator mode."
              );
            }
          } else {
            throw e;
          }
        }
      }

      setScripts({
        strategyScript: extractPineScriptText(composed),
        indicatorScript,
        outputMode,
      });
    } catch (e) {
      setScripts(null);
      if (e instanceof ReportStrategyComposedPineError) {
        setLoadError(e.message);
      } else {
        setLoadError(e instanceof Error ? e.message : "Could not load active Pinescript.");
      }
    } finally {
      setLoading(false);
      if (!composeRevealActive) {
        setCardScriptsPending(false);
      }
    }
  }, [
    active?.last_compose_mode,
    hasGeneratedPine,
    strategy.has_indicator_pine,
    strategyId,
    composeRevealActive,
  ]);

  useEffect(() => {
    if (!hasGeneratedPine || scripts || loading) return;
    void loadScripts();
  }, [hasGeneratedPine, loadScripts, loading, scripts, strategy.updated_at, status]);

  useEffect(() => {
    if (!composeRevealActive || !hasGeneratedPine || scripts || loading) return;
    void loadScripts();
  }, [composeRevealActive, hasGeneratedPine, loadScripts, loading, scripts]);

  useEffect(() => {
    if (!autoOpenSignal) return;
    setDialogOpen(true);
    setDialogMode(autoOpenMode);
    setComposeFooter(null);
    setComposePresentation(null);
    setScripts(null);
    onAutoOpenConsumed?.();
  }, [autoOpenMode, autoOpenSignal, onAutoOpenConsumed]);

  const openDetailsDialog = useCallback(() => {
    setDialogMode("checklist");
    setDialogOpen(true);
  }, []);

  const openComposeDialog = useCallback(() => {
    setComposePresentation(null);
    setDialogMode("compose");
    setDialogOpen(true);
  }, []);

  const openGenerateReportWizard = useCallback(() => {
    setDialogOpen(false);
    const params = new URLSearchParams({ strategy_id: strategyId });
    history.push(`/reports/new?${params.toString()}`);
  }, [history, strategyId]);

  const handleNavigateToPoliciesTab = useCallback(() => {
    onNavigateToPoliciesTab?.();
    setDialogOpen(false);
    window.setTimeout(() => {
      setDialogMode("checklist");
      setComposeFooter(null);
      setComposePresentation(null);
    }, PINE_DETAIL_DIALOG_CLOSE_MS);
  }, [onNavigateToPoliciesTab]);

  const handleEmbeddedComposeClose = useCallback(() => {
    onComposeCancel?.();
    setDialogMode("checklist");
    setComposeFooter(null);
    setComposePresentation(null);
  }, [onComposeCancel]);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      if (dialogMode === "compose" && !composeDismissSuppressedRef.current) {
        onComposeCancel?.();
      }
      composeDismissSuppressedRef.current = false;
      window.setTimeout(() => {
        if (composeInFlight) return;
        setDialogMode("checklist");
        setComposeFooter(null);
        setComposePresentation(null);
      }, PINE_DETAIL_DIALOG_CLOSE_MS);
    }
  }, [composeInFlight, dialogMode, onComposeCancel]);

  const scrollCardIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      cardAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  const startCardComposeSequence = useCallback(() => {
    composeDismissSuppressedRef.current = true;
    setComposeInFlight(true);
    setDialogOpen(false);
    setComposeFooter(null);
    setComposePresentation(null);
    setScripts(null);
    setLoadError(null);
    setCardScriptsPending(true);
    setComposeRevealActive(true);
    setComposeSequence((sequence) => sequence + 1);
    onComposeStarted?.();
    scrollCardIntoView();
  }, [onComposeStarted, scrollCardIntoView]);

  const resetCardComposeSequence = useCallback(() => {
    setComposeInFlight(false);
    setCardScriptsPending(false);
    setComposeRevealActive(false);
    setDialogMode("checklist");
    setComposeFooter(null);
    setComposePresentation(null);
  }, []);

  useEffect(() => {
    if (!composeStartSignal) return;
    startCardComposeSequence();
  }, [composeStartSignal, startCardComposeSequence]);

  useEffect(() => {
    if (!composeFailedSignal) return;
    resetCardComposeSequence();
  }, [composeFailedSignal, resetCardComposeSequence]);

  const handleComposeStart = useCallback(() => {
    startCardComposeSequence();
  }, [startCardComposeSequence]);

  const handleComposeFailed = useCallback(() => {
    resetCardComposeSequence();
  }, [resetCardComposeSequence]);

  const handleRevealComplete = useCallback(() => {
    setComposeRevealActive(false);
    setCardScriptsPending(false);
  }, []);

  const handleComposeSuccess = useCallback(
    async (composedPolicyVersionId: string) => {
      setComposeInFlight(false);
      setComposeFooter(null);
      setComposePresentation(null);
      setDialogMode("checklist");
      await onComposeSuccess?.(composedPolicyVersionId);
    },
    [onComposeSuccess]
  );

  const showScriptsPanel = Boolean(
    hasGeneratedPine &&
      scripts &&
      (scripts.strategyScript.trim() || scripts.indicatorScript.trim())
  );

  const copyText = useCallback(async (text: string, label: string) => {
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${label}`);
    } catch {
      toastError("Copy failed");
    }
  }, []);

  const downloadText = useCallback((text: string, filename: string) => {
    if (!text.trim()) return;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleDeleteComposedPine = useCallback(async () => {
    if (deletingComposedPine) return;

    setDeletingComposedPine(true);
    try {
      await deleteReportStrategyComposedPinescript(strategyId);
      setScripts(null);
      setLoadError(null);
      setCardScriptsPending(false);
      setComposeRevealActive(false);
      setComposeInFlight(false);
      setActivePineScriptTab("strategy");
      setDialogMode("checklist");
      setDeleteDialogOpen(false);
      await onStrategyRefresh?.();
      toast.success("Composed Pinescript removed");
    } catch (e) {
      const description =
        e instanceof ReportsApiError || e instanceof Error
          ? e.message
          : "Could not remove composed Pinescript.";
      toastError("Remove failed", { description });
    } finally {
      setDeletingComposedPine(false);
    }
  }, [deletingComposedPine, onStrategyRefresh, strategyId]);

  const openDeleteComposedPineDialog = useCallback(() => {
    if (deletingComposedPine) return;
    setDeleteDialogOpen(true);
  }, [deletingComposedPine]);

  const headerBadge = activePineAccordionHeaderBadge(active);
  const composeChecklist = useMemo(
    () =>
      buildActiveTradingViewComposeChecklist(strategy, {
        activePolicyVersionCount,
        currentPolicyVersionId,
      }),
    [strategy, activePolicyVersionCount, currentPolicyVersionId]
  );

  const cardSubtitle =
    status === "composed" ? (
      <ActivePineProvenanceText active={active} versions={composeVersions} format="composedPolicyLine" /> || (
        <span className="font-normal text-gray-500 dark:text-white">Composed Pinescript saved. Open details to view scripts.</span>
      )
    ) : status === "stale" ? (
      "Master Pinescript changed. Re-compose to refresh your TradingView script."
    ) : (
      "Click Get started to compose a TradingView Pinescript from one of your saved policies."
    );

  const checklistDialogDescription =
    status === "composed"
      ? "Composed TradingView export for this strategy."
      : status === "stale"
        ? "Re-compose to refresh scripts after master Pinescript changes."
        : "Compose from a saved policy version to generate TradingView Pinescript.";

  const dialogTitle =
    dialogMode === "compose" && composePresentation?.title
      ? composePresentation.title
      : dialogMode === "compose"
        ? "Compose for TradingView"
        : "Active TradingView Pinescript";

  const dialogDescription =
    dialogMode === "compose" && composePresentation?.description != null
      ? composePresentation.description
      : dialogMode === "compose"
        ? COMPOSE_DIALOG_DESCRIPTION
        : checklistDialogDescription;

  const dialogSize =
    dialogMode === "compose"
      ? (composePresentation?.size ?? "compact")
      : showScriptsPanel && scripts
        ? "wide"
        : "compact";

  const checklistComposeButton = (
    <Button
      type="button"
      variant="gradient"
      size="sm"
      data-font="primary"
      data-strategy-active-pine-compose=""
      className="shrink-0"
      disabled={composeChecklist.phase === "before" && !composeChecklist.readyToCompose}
      title={
        composeChecklist.phase === "before" && !composeChecklist.readyToCompose
          ? "Upload and parse master Pinescript and save a policy version first."
          : undefined
      }
      onClick={openComposeDialog}
    >
      <span className="normal-case tracking-normal">Compose TradingView Pinescript</span>
    </Button>
  );

  const checklistGenerateReportButton = (
    <Button
      type="button"
      variant="outline-gray"
      size="sm"
      data-font="primary"
      data-strategy-active-pine-generate-report=""
      className="shrink-0"
      onClick={openGenerateReportWizard}
    >
      <span className="normal-case tracking-normal">Generate a report</span>
    </Button>
  );

  const checklistFooter =
    showComposeChecklist && activePolicyVersionCount < 1 ? (
      <>
        {checklistComposeButton}
        {checklistGenerateReportButton}
      </>
    ) : (
      checklistComposeButton
    );

  const composedViewFooter = useMemo(() => {
    const strategyCode = scripts?.strategyScript.trim() ?? "";
    const indicatorCode = scripts?.indicatorScript.trim() ?? "";
    const outputMode = scripts?.outputMode ?? active?.last_compose_mode;
    const separateMode = isSeparateIndicatorComposeMode(outputMode);
    const viewingIndicator = separateMode && activePineScriptTab === "indicator";
    const activeCode = viewingIndicator ? indicatorCode : strategyCode;
    const canUseActiveCode = Boolean(activeCode) && !loading;
    const copyAriaLabel = viewingIndicator
      ? "Copy indicator overlay"
      : separateMode
        ? "Copy strategy script"
        : "Copy Pinescript";
    const copyToastLabel = viewingIndicator ? "indicator overlay" : separateMode ? "strategy script" : "Pinescript";
    const downloadLabel = viewingIndicator
      ? "Download indicator"
      : separateMode
        ? "Download strategy"
        : "Download";
    const downloadFilename = viewingIndicator
      ? `${strategyId}-indicator-overlay.pine`
      : separateMode
        ? `${strategyId}-strategy.pine`
        : `${strategyId}-composed.pine`;

    return (
      <>
        <PineScriptCopyIconButton
          disabled={!canUseActiveCode}
          ariaLabel={copyAriaLabel}
          onClick={() => void copyText(activeCode, copyToastLabel)}
        />
        <PineScriptDeleteIconButton
          disabled={deletingComposedPine || loading}
          ariaLabel="Remove composed Pinescript"
          onClick={openDeleteComposedPineDialog}
        />
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          data-pine-script-download=""
          className="shrink-0"
          disabled={!canUseActiveCode}
          onClick={() => downloadText(activeCode, downloadFilename)}
        >
          <span className="normal-case tracking-normal">{downloadLabel}</span>
        </Button>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          data-strategy-active-pine-recompose=""
          className="shrink-0"
          onClick={openComposeDialog}
        >
          <span className="normal-case tracking-normal">Re-compose new Pinescript</span>
        </Button>
      </>
    );
  }, [
    active?.last_compose_mode,
    activePineScriptTab,
    copyText,
    deletingComposedPine,
    downloadText,
    openDeleteComposedPineDialog,
    loading,
    openComposeDialog,
    scripts?.indicatorScript,
    scripts?.outputMode,
    scripts?.strategyScript,
    strategyId,
  ]);

  const showCardScriptPanel = hasGeneratedPine || cardScriptsPending || composeRevealActive;
  const cardComposeMode = scripts?.outputMode ?? active?.last_compose_mode;
  const cardScriptTitle = activeComposedScriptTitle(cardComposeMode, isSeparateIndicatorComposeMode(cardComposeMode));
  const cardScriptDescription =
    cardScriptsPending || composeRevealActive
      ? "Generating your TradingView Pinescript."
      : activeComposedScriptDescription(cardComposeMode, "composed");

  const cardScriptPreview = showCardScriptPanel ? (
    <div
      data-strategy-active-pine-card-preview=""
      className="flex min-w-0 flex-col gap-3 animate-in fade-in-0 slide-in-from-top-2 duration-300"
    >
      {composeRevealActive ? (
        <Field className="w-full min-w-0" data-strategy-active-pine-script-field="">
          <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{cardScriptTitle}</FieldLabel>
          <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            {cardScriptDescription}
          </FieldDescription>
          <PineScriptScrambleReveal
            waitingForCode={!scripts?.strategyScript?.trim()}
            code={scripts?.strategyScript ?? ""}
            maxH={PINE_SCRIPT_CARD_MAX_H}
            minScrambleMs={5000}
            sessionKey={composeSequence}
            onRevealComplete={handleRevealComplete}
          />
        </Field>
      ) : null}
      {loadError ? (
        <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
          {loadError}
        </Text>
      ) : null}
      {!composeRevealActive && showScriptsPanel && scripts ? (
        <ActivePineLoadedScripts
          scripts={scripts}
          composeMode={scripts.outputMode ?? active?.last_compose_mode}
          activeTab={activePineScriptTab}
          onActiveTabChange={setActivePineScriptTab}
          codeMaxH={PINE_SCRIPT_CARD_MAX_H}
        />
      ) : null}
    </div>
  ) : null;

  const showCardViewDetailsButton = !composeRevealActive;
  const cardHeaderBadge = composeRevealActive ? null : headerBadge;
  const cardDescription =
    composeRevealActive || cardScriptsPending
      ? "Composing TradingView Pinescript from your saved policy…"
      : cardSubtitle;

  const dialogFooter =
    dialogMode === "compose"
      ? (composePresentation?.footer ?? composeFooter)
      : hasComposedExport
        ? composedViewFooter
        : checklistFooter;

  return (
    <>
      <div ref={cardAnchorRef} className="min-w-0" data-strategy-active-pine-card-anchor="">
        <StrategyPineSectionCard
          data-strategy-pine-card="active"
          title="Active TradingView Pinescript"
          description={cardDescription}
          headerBadge={cardHeaderBadge}
          onViewDetails={openDetailsDialog}
          viewDetailsLabel={showEmptyState ? "Get started" : "View details"}
          showViewDetailsButton={showCardViewDetailsButton}
        >
          {cardScriptPreview}
        </StrategyPineSectionCard>
      </div>

      <StrategyPineDetailDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        keepContentMounted={composeInFlight && dialogMode === "compose"}
        size={dialogSize}
        contentClassName={
          dialogMode === "compose" ? STRATEGY_COMPOSE_DIALOG_CONTENT_CLASS : undefined
        }
        contentProps={
          dialogMode === "compose" ? { "data-strategy-compose-dialog": "" } : undefined
        }
        title={dialogTitle}
        description={dialogDescription}
        footer={dialogFooter}
        showDismissButton={false}
      >
        {dialogMode === "compose" ? (
          <StrategyComposeModal
            key={`active-pine-compose-${autoOpenSignal}`}
            shell="embedded"
            isOpen
            onClose={handleEmbeddedComposeClose}
            onCancel={handleEmbeddedComposeClose}
            onEmbeddedPresentationChange={setComposePresentation}
            strategy={strategy}
            versions={composeVersions}
            currentPolicyVersionId={currentPolicyVersionId ?? null}
            preferredPolicyVersionId={preferredComposePolicyVersionId}
            subtleText={subtleText}
            textColor={textColor}
            borderColor={borderColor}
            parseResult={parseResult}
            onStrategyRefresh={onStrategyRefresh ?? (async () => {})}
            onComposeStart={handleComposeStart}
            onComposeFailed={handleComposeFailed}
            onComposeSuccess={handleComposeSuccess}
            onComposeFooterChange={setComposeFooter}
            onNavigateToPoliciesTab={handleNavigateToPoliciesTab}
          />
        ) : (
          <Box id="strategy-active-pine-panel" className="flex flex-col gap-3">
            {showComposeChecklist ? (
              <ActiveTradingViewPineComposeChecklist checklist={composeChecklist} />
            ) : null}

            {provenanceMismatch ? (
              <Alert
                variant={strategyPineAlertVariant("info")}
                className={STRATEGY_PINE_ALERT_CLASS}
                data-strategy-active-pine-provenance-mismatch=""
              >
                <StrategyPineAlertIcon tone="info" />
                <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                  {provenanceMismatch}
                </AlertDescription>
              </Alert>
            ) : null}

            {status === "stale" ? (
              <Alert
                variant={strategyPineAlertVariant("warning")}
                className={STRATEGY_PINE_ALERT_CLASS}
                data-strategy-active-pine-stale=""
              >
                <StrategyPineAlertIcon tone="warning" />
                <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                  {provenanceParts ? (
                    <>
                      Last composed from{" "}
                      <ActivePineProvenanceText
                        active={active}
                        versions={composeVersions}
                        format="policyNameOnly"
                      />
                      . Master Pinescript was updated afterward. Composed downloads are disabled
                      until you re-run compose.
                    </>
                  ) : (
                    <>
                      Master Pinescript changed after your last compose. Re-run TradingView export
                      to refresh.
                    </>
                  )}
                </AlertDescription>
              </Alert>
            ) : null}

            {hasGeneratedPine && dialogOpen && loading && !showScriptsPanel ? (
              <HStack spacing={2} py={1}>
                <Spinner size="sm" color="#38BBD1" thickness="2px" />
                <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
                  Loading active Pinescript…
                </Text>
              </HStack>
            ) : null}

            {hasGeneratedPine && loadError ? (
              <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
                {loadError}
              </Text>
            ) : null}

            {showScriptsPanel && scripts ? (
              <ActivePineLoadedScripts
                scripts={scripts}
                composeMode={scripts.outputMode ?? active?.last_compose_mode}
                activeTab={activePineScriptTab}
                onActiveTabChange={setActivePineScriptTab}
              />
            ) : null}

            {showComposeChecklist ? (
              <ActiveTradingViewPineComposeRemainingAlert checklist={composeChecklist} />
            ) : null}
          </Box>
        )}
      </StrategyPineDetailDialog>

      <DeleteActivePinescriptDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        isDeleting={deletingComposedPine}
        onConfirm={() => void handleDeleteComposedPine()}
      />
    </>
  );
}
